import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { formatServiceDay, type MenuOffer } from '@mealdesk/shared';
import PointSheet from '../components/PointSheet.js';
import { useT } from '../lib/i18n.js';
import { EmptyState, MealChip } from '../components/ui.js';
import {
  offerKey,
  useDeliveryPoints,
  useMoney,
  usePlaceOrders,
  useProfile,
  useWeek,
  type BatchResult,
} from '../lib/ordering.js';

/**
 * Where the days you picked are going.
 *
 * A second page rather than a line under every row: choosing a place for five
 * days is one decision made once, and asking it inside the menu turned the
 * list somebody was still browsing into a form. Here the days stand together,
 * which is also the only way to see that Thursday is the odd one out.
 *
 * One control sets every day; each row can then disagree with it.
 */
export default function BatchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const week = useWeek();
  const points = useDeliveryPoints();
  const profile = useProfile();
  const place = usePlaceOrders();
  const money = useMoney();
  const { t } = useT();

  const keys = (location.state as { keys?: string[] } | null)?.keys ?? [];

  const [allPoint, setAllPoint] = useState('');
  const [dayPoints, setDayPoints] = useState<Record<string, string>>({});
  // null = closed, '' = choosing for every day, otherwise that day's key.
  const [asking, setAsking] = useState<string | null>(null);
  const [result, setResult] = useState<BatchResult | null>(null);

  const offers = week
    .flatMap((day) => day.offers)
    .filter((offer) => keys.includes(offerKey(offer)));

  const fallback = allPoint || profile?.defaultPointId || points[0]?.id || '';
  const pointFor = (offer: MenuOffer) => dayPoints[offerKey(offer)] ?? fallback;
  const nameOf = (id: string) => points.find((p) => p.id === id)?.name ?? '';
  const noteOf = (id: string) => points.find((p) => p.id === id)?.note ?? '';
  const exceptions = offers.filter((o) => pointFor(o) !== fallback).length;

  const total = money(
    offers.reduce((sum, o) => sum + o.price, 0),
    offers.reduce((sum, o) => sum + o.employeeShare, 0),
    offers[0]?.currency ?? 'USD',
  );

  const submit = () => {
    place.mutate(
      { items: offers.map((offer) => ({ offer, deliveryPointId: pointFor(offer) })) },
      { onSuccess: (data) => setResult(data) },
    );
  };

  // Arriving here with nothing — a refresh, or a pasted link.
  if (offers.length === 0 && !result) {
    return (
      <div className="px-[22px] pt-[calc(22px+env(safe-area-inset-top))] md:px-2 md:pt-7">
        <EmptyState
          title={t('Nothing picked')}
          body={t('Choose the days you want on the weekly menu, then set where they go.')}
        />
        <div className="mt-4">
          <Link
            to="/menu"
            className="flex h-[52px] items-center justify-center rounded-control bg-brand text-[15px] font-extrabold text-white md:text-[16.5px]"
          >
            {t('Back to the menu')}
          </Link>
        </div>
      </div>
    );
  }

  if (result) {
    const failed = result.items.filter((i) => !i.placed);
    return (
      <div className="px-[22px] pt-[22px] pb-8 md:px-2 md:pt-7">
        <h2 className="text-[25px] font-extrabold md:text-[30px]">
          {result.placed} meal{result.placed === 1 ? '' : 's'} ordered
        </h2>

        {/* Named per day, because "some of it failed" is not something anybody
            can act on. */}
        {failed.length > 0 && (
          <div className="mt-4 rounded-tile bg-warn-soft px-4 py-3.5 text-[13.5px] text-warn-ink md:text-[15px]">
            <b className="block">{t('These could not be ordered:')}</b>
            <ul className="mt-1.5 space-y-1">
              {failed.map((item) => (
                <li key={`${item.serviceDate}-${item.mealType}`}>
                  {formatServiceDay(item.serviceDate)} — {item.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-5 flex flex-col gap-2.5">
          <Link
            to="/history"
            className="flex h-[52px] items-center justify-center rounded-control bg-brand text-[15px] font-extrabold text-white md:text-[16.5px]"
          >
            {t('See my orders')}
          </Link>
          <Link
            to="/menu"
            className="flex h-[52px] items-center justify-center rounded-control border border-line-strong text-[15px] font-extrabold md:text-[16.5px]"
          >
            Back to the menu
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="px-[22px] pt-[22px] md:px-2 md:pt-7">
        <button
          type="button"
          onClick={() => navigate('/menu')}
          className="mb-3 text-[13.5px] font-bold text-muted md:text-[15px]"
        >
          ‹ {t('Back to the menu')}
        </button>

        <h2 className="text-[25px] font-extrabold md:text-[30px]">{t('Where do these go?')}</h2>
        <p className="mt-1.5 text-[13.5px] text-muted md:text-[15px]">
          {offers.length} meal{offers.length === 1 ? '' : 's'}
          {total ? ` · ${total}` : ''}
        </p>

        {/* The decision most people make once. */}
        <div className="mt-4 rounded-card border border-line-strong bg-panel px-[18px] py-4">
          <span className="text-[13px] font-extrabold tracking-[0.05em] text-subtle uppercase md:text-[14.5px]">
            {t('Every day')}
          </span>
          <button
            type="button"
            onClick={() => setAsking('')}
            className="mt-1.5 flex w-full items-center gap-3 text-left"
          >
            <span className="min-w-0 flex-1">
              <b className="block truncate text-[16px] md:text-[17px]">
                {fallback ? nameOf(fallback) : t('Choose a pickup point')}
              </b>
              <span className="block truncate text-[13px] text-muted md:text-[14.5px]">
                {fallback ? noteOf(fallback) : t('Nothing can be ordered until one is set')}
              </span>
            </span>
            <span className="shrink-0 text-[13.5px] font-extrabold text-brand-ink md:text-[15px]">
              {t('Change')}
            </span>
          </button>
        </div>

        {exceptions > 0 && (
          <p className="mt-2.5 text-[13px] text-muted md:text-[14.5px]">
            {exceptions} {t('set to somewhere else')}
          </p>
        )}
      </div>

      <div className="px-[22px] pt-5 pb-[120px] md:px-2 md:pt-7">
        <b className="mb-[11px] block text-[13px] font-extrabold tracking-[0.07em] text-subtle uppercase md:text-[14.5px]">
          {t('The days you picked')}
        </b>

        <div className="flex flex-col gap-2.5 md:grid md:grid-cols-2 md:gap-3.5">
          {offers.map((offer) => {
            const key = offerKey(offer);
            const own = dayPoints[key] !== undefined && dayPoints[key] !== fallback;
            return (
              <div
                key={key}
                className="rounded-card bg-white shadow-[0_4px_14px_rgba(38,64,28,.05)]"
              >
                <div className="px-[17px] pt-[15px] pb-3">
                  <MealChip
                    mealType={offer.mealType}
                    when={capitalise(formatServiceDay(offer.serviceDate))}
                  />
                  <b className="mt-2 block text-[15.5px] tracking-[-0.03em] text-pretty md:text-[17px]">
                    {offer.title}
                  </b>
                  <span className="text-[13px] text-muted md:text-[14.5px]">
                    {offer.restaurantName}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => setAsking(key)}
                  className="flex w-full items-center gap-2 border-t border-line-soft px-[17px] py-3 text-left text-[13px] md:text-[14.5px]"
                >
                  <span aria-hidden>📍</span>
                  <span className={`min-w-0 flex-1 truncate ${own ? 'font-bold text-ink' : 'text-muted'}`}>
                    {nameOf(pointFor(offer)) || t('No pickup point')}
                  </span>
                  <span className="shrink-0 font-extrabold text-brand-ink">{t('Change')}</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-[calc(var(--tabbar)+10px)] z-30 mx-auto max-w-[430px] px-[22px] md:bottom-6">
        <button
          type="button"
          onClick={submit}
          disabled={place.isPending || !fallback}
          className="flex h-[56px] w-full items-center justify-center rounded-control bg-brand text-[16px] font-extrabold text-white shadow-[0_10px_30px_rgba(38,64,28,.18)] disabled:opacity-50 md:text-[17px]"
        >
          {place.isPending
            ? 'Ordering…'
            : `Order ${offers.length} meal${offers.length === 1 ? '' : 's'}`}
        </button>
      </div>

      {asking !== null && (
        <PointSheet
          title={asking === '' ? t('Every day goes to') : t('Just this day')}
          note={
            asking === ''
              ? t('Sets all of them. A single day can still be changed on its own card.')
              : t('Only this meal moves. The rest stay where they are.')
          }
          points={points}
          value={asking === '' ? fallback : (dayPoints[asking] ?? fallback)}
          onPick={(id) => {
            if (asking === '') {
              // Choosing for everything means exactly that.
              setAllPoint(id);
              setDayPoints({});
            } else {
              setDayPoints((prev) => ({ ...prev, [asking]: id }));
            }
          }}
          onClose={() => setAsking(null)}
        />
      )}
    </>
  );
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
